// passiveAssetClassification.js
// Classifies a single normalized passive-asset read. Because the current API
// is raw-read oriented, we deliberately avoid claiming "Wrong Zone" unless
// asset master + zone rules + flight/task context are all present.

export const CLASSIFICATION = {
  NORMAL: 'NORMAL',
  LIKELY_PASS_BY: 'LIKELY_PASS_BY',
  NEEDS_REVIEW: 'NEEDS_REVIEW',
  POSSIBLE_WRONG_ZONE: 'POSSIBLE_WRONG_ZONE',
  INSUFFICIENT_CONTEXT: 'INSUFFICIENT_CONTEXT'
};

export const CLASSIFICATION_LABEL = {
  NORMAL: 'Normal',
  LIKELY_PASS_BY: 'Likely Pass-by',
  NEEDS_REVIEW: 'Needs Review',
  POSSIBLE_WRONG_ZONE: 'Possible Wrong Zone',
  INSUFFICIENT_CONTEXT: 'Insufficient Context'
};

export const CLASSIFICATION_COLOR = {
  NORMAL: 'green',
  LIKELY_PASS_BY: 'gold',
  NEEDS_REVIEW: 'orange',
  POSSIBLE_WRONG_ZONE: 'red',
  INSUFFICIENT_CONTEXT: 'default'
};

const WEAK_RSSI_THRESHOLD = -85;

export function classifyPassiveAssetRead(read, context = {}) {
  const checks = [];

  if (!read) {
    return {
      classification: CLASSIFICATION.INSUFFICIENT_CONTEXT,
      reason: 'No read data available.',
      checks
    };
  }

  const hasAssetMaster = Boolean(context.assetMaster);
  const hasZoneRules = Boolean(context.zoneRules);
  const hasFlightData = Boolean(context.flightData);

  checks.push({ label: 'Raw sensor read available', passed: true });
  checks.push({ label: 'Asset master available', passed: hasAssetMaster });
  checks.push({ label: 'Zone rules available', passed: hasZoneRules });
  checks.push({ label: 'Flight / task context available', passed: hasFlightData });

  if (!hasAssetMaster && !hasZoneRules && !hasFlightData) {
    return {
      classification: CLASSIFICATION.INSUFFICIENT_CONTEXT,
      reason:
        'Only raw sensor data is available. Asset master, zone rules, and flight context are required for reliable wrong-zone detection.',
      checks
    };
  }

  if (read.rssi !== null && read.rssi !== undefined && Number(read.rssi) < WEAK_RSSI_THRESHOLD) {
    return {
      classification: CLASSIFICATION.LIKELY_PASS_BY,
      reason: `Weak signal (${read.rssi} dBm) suggests the asset only passed near the reader.`,
      checks
    };
  }

  if (hasAssetMaster && hasZoneRules) {
    const expectedZone = context.assetMaster?.expectedZone;
    const detectedZone = read.zone && read.zone !== '-' ? read.zone : null;

    if (expectedZone && detectedZone && expectedZone !== detectedZone) {
      if (hasFlightData && context.flightData?.justifiesZone === detectedZone) {
        return {
          classification: CLASSIFICATION.NORMAL,
          reason: 'Detected zone differs from expected, but flight/task context justifies the location.',
          checks
        };
      }
      const repeated = Number(context.repeatedDetectionCount || 0) > 1;
      if (repeated && !hasFlightData) {
        return {
          classification: CLASSIFICATION.POSSIBLE_WRONG_ZONE,
          reason: `Repeated detections in ${detectedZone} while expected zone is ${expectedZone}, with no operational context.`,
          checks
        };
      }
      return {
        classification: CLASSIFICATION.NEEDS_REVIEW,
        reason: `Detected zone (${detectedZone}) does not match expected zone (${expectedZone}).`,
        checks
      };
    }
  }

  return {
    classification: CLASSIFICATION.NORMAL,
    reason: 'Read appears consistent with available context.',
    checks
  };
}

// Convenience: count occurrences of an asset identifier in a list of reads so
// callers can pass repeatedDetectionCount into classifyPassiveAssetRead.
export function buildRepeatIndex(reads) {
  const index = new Map();
  reads.forEach((r) => {
    if (!r || !r.assetIdentifier) return;
    index.set(r.assetIdentifier, (index.get(r.assetIdentifier) || 0) + 1);
  });
  return index;
}
